import React, { useEffect, useRef } from 'react';
import { TIMELINE_MAX_SCALE, TIMELINE_MIN_SCALE } from '@/components/Packaging/constants';
import { setTimelineScale } from '@/redux/slices/packagingSlice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectPackagingTimelineScale } from '@/redux/selectors/packagingSelectors';

/**
 * Main purpose of this hook is to provide functionality to zoom the timeline by scrolling over it (with mouse scroll or
 * by scrolling vertically with the touchpad) with Ctrl (Cmd) button pressed.
 * The additional feature of this hook is to preserve the position of the timeline while zooming. Whatever is under the
 * pointer (whatever place of the timeline) will stay under the pointer during the scaling. It's accomplished by
 * automatically scrolling the timeline.
 */
export const useScrollZoom = () => {
  const dispatch = useAppDispatch();
  const scale = useAppSelector(selectPackagingTimelineScale);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const pointerOnTimelineXRef = useRef(0); // Pointer positions relatively to the timeline
  const pointerClientXRef = useRef(0); // Pointer position relatively to the screen

  useEffect(() => {
    if (!wrapperRef.current) {
      throw new Error('wrapper element was not found');
    }

    const handleWheel = (e: WheelEvent) => {
      if (!wrapperRef.current) {
        throw new Error('wrapper element was not found');
      }

      if (!e.ctrlKey && !e.metaKey) {
        return;
      }

      e.preventDefault();

      // use easing quadratic easing function (the higher scale is the bigger is multiplier)
      const multiplier = (scale / TIMELINE_MAX_SCALE / 4) * (0.01 ^ 2);

      const oldScale = scale;
      let newScale: number;

      if (e.deltaY > 0) {
        newScale = Math.max(TIMELINE_MIN_SCALE, scale - Math.abs(e.deltaY) * multiplier);
      } else {
        newScale = Math.min(TIMELINE_MAX_SCALE, scale + Math.abs(e.deltaY) * multiplier);
      }

      dispatch(setTimelineScale(newScale));

      const timelineXRelative = pointerOnTimelineXRef.current / oldScale;
      const newTimelineX = newScale * timelineXRelative;

      const scrollLeft = newTimelineX - pointerClientXRef.current;

      wrapperRef.current.scroll({ left: scrollLeft });

      pointerOnTimelineXRef.current = (pointerOnTimelineXRef.current / oldScale) * newScale;
    };

    wrapperRef.current.addEventListener('wheel', handleWheel);

    return () => {
      wrapperRef.current?.removeEventListener('wheel', handleWheel);
    };
  }, [scale]);

  const onMouseMove: React.MouseEventHandler = (e) => {
    if (!wrapperRef.current) {
      throw new Error('wrapper element was not found');
    }

    pointerClientXRef.current = e.clientX;
    pointerOnTimelineXRef.current = e.clientX + wrapperRef.current.scrollLeft;
  };

  const onScroll: React.UIEventHandler = (e) => {
    pointerOnTimelineXRef.current = pointerClientXRef.current + e.currentTarget.scrollLeft;
  };

  return {
    wrapperRef,
    listeners: {
      onMouseMove,
      onScroll
    }
  };
};
